import { Box, Stack, Typography, Tooltip, useTheme } from '@mui/material'
import { PackageIcon, PackageCheckIcon, TagIcon, PrinterIcon, TruckIcon, BoxIcon, XIcon } from 'lucide-react'

import { Button, ShippingStepper } from '@repo/ui'
import { SHIPPING_BOX_PRESETS, type ShippingBoxPresetId } from 'constants/shippingBoxPresets'

import { ProductCell } from 'components'

import type { OrderShipment } from 'store/orders/ordersStore'

// Open the label and trigger the browser print dialog. Cross-origin labels may
// block the programmatic print() from the opener — the label still opens so the
// user can print manually (Cmd+P). Exported so the Orders list kebab's "Print
// Shipping Label" action reuses the same behavior.
export const printLabel = (url: string) => {
  const w = window.open(url, '_blank')
  if (!w) return
  w.addEventListener('load', () => {
    try {
      w.focus()
      w.print()
    } catch {
      /* cross-origin — user prints manually */
    }
  })
}

// VRG-138 (VERA-891): maps a shipment's own fulfillment status to the 3-step
// ShippingStepper index — each shipment fulfills INDEPENDENTLY, so it maps off
// `order_shipments.status` rather than the order's Shopify `fulfillmentStatus`.
const shipmentToStep = (status: string): number => {
  if (status === 'delivered') return 2
  if (status === 'shipped' || status === 'in_transit') return 1
  return 0 // ready — nothing shipped yet
}

interface ShipmentCardProps {
  shipment: OrderShipment
  /** The order line items in this shipment (self-documenting: the box's contents). */
  products: Array<{ id: string; title: string; image?: string; variant?: { sku?: string } }>
  onFulfill: () => void
  onBuyLabel: () => void
  /** Remove a product (line item) from this shipment. */
  onRemoveProduct?: (lineItemId: string) => void
  removingLineItemId?: string | null
  isFulfilling?: boolean
  isBuyingLabel?: boolean
  /**
   * Render the content WITHOUT the bordered/padded card box — used when this is
   * the SOLE shipment and it sits directly inside the Fulfillment card (so it
   * isn't a card-in-a-card). Multiple shipments each keep their own box.
   */
  flush?: boolean
}

export const ShipmentCard = (props: ShipmentCardProps) => {
  const { shipment, products, onFulfill, onBuyLabel, onRemoveProduct, removingLineItemId, isFulfilling, isBuyingLabel, flush } = props
  const theme = useTheme()

  const alreadyFulfilled = shipment.status !== 'ready'
  const hasProducts = products.length > 0

  // Box preset + summed weight (set at create-shipment time). The box IS the
  // shipment's identity; the products below ARE its packages (product = package).
  const boxPreset = shipment.boxPreset as ShippingBoxPresetId | null
  const boxLabel = boxPreset && SHIPPING_BOX_PRESETS[boxPreset] ? SHIPPING_BOX_PRESETS[boxPreset].label : 'Shipment'
  const weight = shipment.weightOz != null ? `${Number(shipment.weightOz)} oz` : null

  // Top-right actions: View label + the state action (Buy label / Mark
  // fulfilled / "Fulfilled" indicator), horizontally grouped.
  const actions = (
    <Stack direction="row" spacing={3} alignItems="center">
      {/* Done state — a "Fulfilled" indicator, not a disabled button. Sits
          LEFT of the action buttons: it's status, and status reads before the
          things you can still do to the shipment. */}
      {alreadyFulfilled && (
        <Stack direction="row" spacing={1} alignItems="center">
          <PackageCheckIcon strokeWidth={1} size={16} color={theme.palette.success.main} />
          <Typography variant="body3" sx={{ color: 'success.main', fontWeight: 500 }}>
            Fulfilled
          </Typography>
        </Stack>
      )}

      {shipment.labelUrl && (
        <Button
          disableElevation
          variant="secondary"
          startIcon={<PrinterIcon strokeWidth={1} size={14} />}
          onClick={() => printLabel(shipment.labelUrl!)}
        >
          Print label
        </Button>
      )}

      {/* Tracking → the carrier's tracking page (once shipped with a URL). */}
      {shipment.trackingUrl && (
        <Button
          disableElevation
          variant="secondary"
          startIcon={<TruckIcon strokeWidth={1} size={14} />}
          onClick={() => window.open(shipment.trackingUrl!, '_blank')}
        >
          Tracking
        </Button>
      )}

      {/* A shipment must have a label before it can be fulfilled: no label ->
          "Buy label"; once purchased -> "Fulfill". */}
      {(shipment.labelStatus === 'none' || shipment.labelStatus === 'failed') && !alreadyFulfilled && (
        <Button
          disableElevation
          variant="primary"
          loading={isBuyingLabel}
          startIcon={<TagIcon strokeWidth={1} size={14} />}
          onClick={onBuyLabel}
        >
          Buy label
        </Button>
      )}

      {shipment.labelStatus === 'purchased' && !alreadyFulfilled && (
        <Tooltip title={!hasProducts ? 'Add products to this shipment before fulfilling it.' : ''}>
          <span>
            <Button
              disableElevation
              variant="primary"
              loading={isFulfilling}
              disabled={!hasProducts}
              startIcon={<PackageCheckIcon strokeWidth={1} size={14} />}
              onClick={onFulfill}
            >
              Fulfill
            </Button>
          </span>
        </Tooltip>
      )}

    </Stack>
  )

  const content = (
    <>
      <Stack spacing={4}>
        {/* Header: box size + summed weight on the LEFT; actions on the RIGHT. */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3} flexWrap="wrap">
          <Stack direction="row" spacing={2} alignItems="center">
            <BoxIcon strokeWidth={1} size={16} />
            <Typography variant="body2" fontWeight="medium">
              {boxLabel}
            </Typography>
            {weight && (
              <Typography variant="body3" color="text.secondary">
                · {weight}
              </Typography>
            )}
          </Stack>
          {actions}
        </Stack>

        <ShippingStepper
          steps={[
            { label: 'Shipped', icon: <PackageIcon strokeWidth={1} /> },
            { label: 'On the way', icon: <PackageIcon strokeWidth={1} /> },
            { label: 'Delivered', icon: <PackageCheckIcon strokeWidth={1} /> },
          ]}
          activeStep={shipmentToStep(shipment.status)}
        />

        {/* The products packed in this box (below the progress bar) — the box's
            contents. The product IS the package, so no separate package chips.
            Each can be removed back to the order's staging area (until shipped). */}
        {products.length > 0 && (
          <Stack spacing={2}>
            {products.map(product => (
              <Stack key={product.id} direction="row" alignItems="center" spacing={3}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ProductCell
                    product={{
                      title: product.title,
                      sku: product.variant?.sku,
                      images: product.image ? [product.image] : [],
                    }}
                    size={40}
                  />
                </Box>
                {!alreadyFulfilled && onRemoveProduct && (
                  <Button
                    variant="text"
                    loading={removingLineItemId === product.id}
                    startIcon={<XIcon strokeWidth={1} size={14} />}
                    onClick={() => onRemoveProduct(product.id)}
                    sx={{ flexShrink: 0, color: 'text.secondary' }}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        )}

      </Stack>
    </>
  )

  // Flush (sole shipment inside the Fulfillment card) — no card box.
  if (flush) return content

  return (
    <Box
      sx={{
        // Match the shared Card surface (background + border + radius) so the
        // shipment card reads the same as the Products card below it.
        border: `1px solid ${theme.palette.grey[100]}`,
        borderRadius: 3,
        bgcolor: 'background.paper',
        p: 6,
      }}
    >
      {content}
    </Box>
  )
}
